import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { RepairCodeAdvancedFilterComponent } from '../repair-code-advanced-filter/repair-code-advanced-filter.component';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { ToastrService } from '../../../services/toastr-service';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { RepairCodeFilterForm } from '../../../form-model/repair-code-filter.form.model';
import { RepairCodeSearchTextForm } from '../../../form-model/repair-code-search-text.form.model';
import { environment } from '../../../../environments/environment';
import { RepairCodeService } from '../../../services/repair-code.service';
import { DownloadFilterCriteria, toFilter, toEqualsFilter } from '../../../models/download-filter.model';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';

type FilterValue = string | string[] | boolean[];

@Component({
  selector: 'app-repair-code-filters',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, RepairCodeAdvancedFilterComponent, TagWithOverflowComponent],
  templateUrl: './repair-code-filters.component.html',
  styleUrl: './repair-code-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairCodeFiltersComponent implements AfterViewInit {
  showAdvanceFilters: boolean = false;
  isDownload: boolean = false;
  canAddNewRepairCode = this.authPolicies.canAddRepairCode && environment.Features.EnableRepairCode;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairCodeFilterForm: FormGroup<RepairCodeFilterForm>;
    repairCodeSearchTextForm: FormGroup<RepairCodeSearchTextForm>;
  }>;
  taggedFilters: { key: string; value: FilterValue }[] = [];

  @ViewChild(RepairCodeAdvancedFilterComponent) advanceFilterComponent!: RepairCodeAdvancedFilterComponent;

  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly repairCodeService: RepairCodeService,
  ) {}

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private ensureFlagControlsExist(): void {
    const filterForm = this.filterFormGroup.controls.repairCodeFilterForm;
    const searchForm = this.filterFormGroup.controls.repairCodeSearchTextForm;
    if (!filterForm.get('applyRepairCodeFilter')) {
      filterForm.addControl('applyRepairCodeFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!searchForm.get('appliedRepairCodeSearchTextFilter')) {
      searchForm.addControl('appliedRepairCodeSearchTextFilter', this.formBuilder.control<boolean | null>(null));
    }
    // Add the advanced-filter controls here so they exist on the form before
    // setFilterFormValue() runs — regardless of whether the modal has been opened.
    // RepairCodeAdvancedFilterComponent.addControlsToFormGroup() guards with
    // if (!form.get(...)) so there will be no duplicate-add.
    if (!filterForm.get('repairModeList')) {
      filterForm.addControl('repairModeList', this.formBuilder.control<string[] | null>(null));
    }
    if (!filterForm.get('repairCodeList')) {
      filterForm.addControl('repairCodeList', this.formBuilder.control<string[]>([]));
    }
    if (!filterForm.get('description')) {
      filterForm.addControl('description', this.formBuilder.control<string | null>(null));
    }
    if (!filterForm.get('isActive')) {
      filterForm.addControl('isActive', this.formBuilder.control<string[]>([]));
    }
    if (!filterForm.get('allowParts')) {
      filterForm.addControl('allowParts', this.formBuilder.control<string[]>([]));
    }
    if (!filterForm.get('multipleEq')) {
      filterForm.addControl('multipleEq', this.formBuilder.control<string[]>([]));
    }
  }

  private setFilterFormValue(): void {
    const filterValue = this.repairCodeService.getRepairCodeFilterFormValue();
    if (filterValue) {
      // emitEvent:false prevents the applyRepairCodeFilter subscription from firing
      // (which would clear the service value in its else-branch before we re-apply)
      this.filterFormGroup.controls.repairCodeFilterForm.patchValue(filterValue, { emitEvent: false });
      this.updateSelectedFiltersSection();
      // Defer the trigger so the grid's ngAfterViewInit subscription is set up first
      setTimeout(() => {
        this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
      });
    }
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')!.valueChanges.subscribe((value) => {
      if (value) {
        this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')!.setValue(null, { emitEvent: false });
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.repairCodeService.setRepairCodeFilterFormValue(this.filterFormGroup.controls.repairCodeFilterForm.getRawValue());
      }
      // Note: we intentionally do NOT clear the service filter when value is falsy —
      // the saved filter is only explicitly cleared by user actions (clearAllSelectedFilters)
      // or when navigating away from the repair-code section (RepairCodeListComponent.ngOnDestroy).
    });
    this.filterFormGroup.controls.repairCodeSearchTextForm.get('appliedRepairCodeSearchTextFilter')!.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }

  unselectMode(): (_: string) => void {
    return (mode: string) => {
      const key = this.taggedFilters.find((x) => x.key === 'Mode');
      const control = this.filterFormGroup.controls.repairCodeFilterForm.get('repairModeList');
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((m) => m !== mode);
      if (key) {
        key.value = updatedValue;
      }
      control?.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectRepairCode(): (_: string) => void {
    return (code: string) => {
      const key = this.taggedFilters.find((x) => x.key === 'Repair Code');
      const control = this.filterFormGroup.controls.repairCodeFilterForm.get('repairCodeList');
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((c) => c !== code);
      if (key) {
        key.value = updatedValue;
      }
      control?.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectDescription(): void {
    this.filterFormGroup.controls.repairCodeFilterForm.get('description')?.setValue(null);
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectRepairActive(status: string): void {
    const currentValues: string[] = this.filterFormGroup.controls.repairCodeFilterForm.get('isActive')?.value || [];
    const updatedValues = currentValues.filter((s) => s !== status);
    this.filterFormGroup.controls.repairCodeFilterForm.get('isActive')?.setValue(updatedValues);
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectAllowParts(status: string): void {
    const currentValues: string[] = this.filterFormGroup.controls.repairCodeFilterForm.get('allowParts')?.value || [];
    const updatedValues = currentValues.filter((s) => s !== status);
    this.filterFormGroup.controls.repairCodeFilterForm.get('allowParts')?.setValue(updatedValues);
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectMultipleEq(status: string): void {
    const currentValues: string[] = this.filterFormGroup.controls.repairCodeFilterForm.get('multipleEq')?.value || [];
    const updatedValues = currentValues.filter((s) => s !== status);
    this.filterFormGroup.controls.repairCodeFilterForm.get('multipleEq')?.setValue(updatedValues);
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal(): void {
    this.showAdvanceFilters = true;
  }

  onDownload(): void {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.repairCodeService.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('repair-code'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.repairCodeFilterForm;

    const modes = form.controls.repairModeList?.value;
    if (modes && modes.length > 0) {
      filters.push(toFilter('repair_mode', modes));
    }

    const codes = form.controls.repairCodeList?.value;
    if (codes && codes.length > 0) {
      filters.push(toFilter('repair_code', codes));
    }

    const description = form.controls.description?.value;
    if (description) {
      filters.push(toEqualsFilter('description', description));
    }

    const isActiveValues = form.controls.isActive?.value;
    if (isActiveValues?.length === 1) {
      filters.push(toEqualsFilter('is_active', isActiveValues[0] === 'Y' ? '1' : '0'));
    }

    const allowPartsValues = form.controls.allowParts?.value;
    if (allowPartsValues?.length === 1) {
      filters.push(toEqualsFilter('allow_parts', allowPartsValues[0] === 'Y' ? '1' : '0'));
    }

    const multipleEqValues = form.controls.multipleEq?.value;
    if (multipleEqValues?.length === 1) {
      filters.push(toEqualsFilter('multiple_eq', multipleEqValues[0] === 'Y' ? '1' : '0'));
    }

    return filters;
  }

  closeAdvanceFiltersModal(): void {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      // Restore form to the last APPLIED state so cancelling the modal doesn't lose previous selections
      const lastApplied = this.repairCodeService.getRepairCodeFilterFormValue();
      const form = this.filterFormGroup.controls.repairCodeFilterForm;
      form.patchValue(
        {
          repairModeList: lastApplied?.repairModeList ?? null,
          repairCodeList: lastApplied?.repairCodeList ?? null,
          description: lastApplied?.description ?? null,
          isActive: lastApplied?.isActive ?? null,
          allowParts: lastApplied?.allowParts ?? null,
          multipleEq: lastApplied?.multipleEq ?? null,
        },
        { emitEvent: false },
      );
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
    this.repairCodeService.setRepairCodeFilterFormValue(null);
    this.updateSelectedFiltersSection();
  }

  updateSelectedFiltersSection(): void {
    const form = this.filterFormGroup.controls.repairCodeFilterForm;
    this.markAsTagged('Mode', form.get('repairModeList')?.value ?? null);
    this.markAsTagged('Repair Code', form.get('repairCodeList')?.value ?? null);
    this.markAsTagged('Description', form.get('description')?.value ?? null);
    this.markAsTagged('Repair Active', form.get('isActive')?.value ?? null);
    this.markAsTagged('Parts Allowed', form.get('allowParts')?.value ?? null);
    this.markAsTagged('Multiple Eq', form.get('multipleEq')?.value ?? null);
  }

  markAsTagged(key: string, newValue: FilterValue | null): void {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string): void {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string): FilterValue | null {
    return getSharedFilterValue(this.taggedFilters, key) as FilterValue | null;
  }

  getTaggedModes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Mode');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  getTaggedRepairCodes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Repair Code');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  getTaggedRepairActive(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Repair Active');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  getTaggedAllowParts(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Parts Allowed');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  getTaggedMultipleEq(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Multiple Eq');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onAddNewRepairCodeClicked(): void {
    this.router.navigate(['/master-data/repair-code/add-new']);
  }
}
